import { Add } from "@mui/icons-material";
import { Box, Button, Stack, TextField, MenuItem, Grid } from "@mui/material";
import {
  ColumnVisibilityFilter,
  SearchFilter,
  SectionHeader,
} from "@shared/components";
import { FormActions } from "@shared/utils/constant";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ColumnOptions } from "../utils/constants";
import {
  ColumnKey,
  FilterField,
  PartnersFormValues,
  PartnersHeaderProps,
} from "../utils/types";
import PartnerForm from "./PartnerForm";
import { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import { useCreateNewPartner } from "../services/useCreateNewPartner";
import { useGetAllPartners } from "../services/useGetAllPartners";
import { useGetAllPartnerChannels } from "@modules/partner-channel/services/useGetAllPartnerChannels";
import { Can } from "@core/casl-provider";

const filterFields: FilterField[] = [
  {
    id: "referrerId-search",
    name: "referrerID",
    label: "Referrer ID",
    placeholder: "Search referrer ID...",
    clearAriaLabel: "clear referrer ID filter",
  },
  {
    id: "partners-search",
    name: "username",
    label: "Search Partners",
    placeholder: "Search partners...",
    clearAriaLabel: "clear partners search",
  },
  {
    id: "platform-filter",
    name: "platform",
    label: "Platform",
    placeholder: "Search platform...",
    clearAriaLabel: "clear platform filter",
  },
  {
    id: "invoice-party-filter",
    name: "invoiceParty",
    label: "Invoice Party",
    placeholder: "Search invoice party...",
    clearAriaLabel: "clear invoice party filter",
  },
];

const PartnersHeader = ({
  visibleColumns,
  setVisibleColumns,
}: PartnersHeaderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionState, setActionState] = useState<{
    action: FormActions.ADD | "";
    payload: PartnersFormValues | object;
  }>({
    action: "",
    payload: {} as PartnersFormValues,
  });

  const { mutateAsync: createPartnerAsync, isPending } = useCreateNewPartner();

  const { data: partnersList } = useGetAllPartners({
    data: {
      pageNumber: "1",
      pageSize: "10000",
      includeInactive: true,
    },
  });

  const { data: partnerChannelsResp } = useGetAllPartnerChannels({
    data: { pageNumber: "1", pageSize: "1000", includeInactive: true },
  });

  const partnerChannels = partnerChannelsResp?.data || [];

  const selectedPartnerChannel = searchParams.get("partnerChannel") || "";

  const handlePartnerChannelChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("partnerChannel", value);
    } else {
      next.delete("partnerChannel");
    }
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const clearActionState = () => {
    setActionState({ action: "", payload: {} });
  };

  const handleSubmit = async (
    values: PartnersFormValues & { id?: string | number },
    helpers: FormikHelpers<PartnersFormValues & { id?: string | number }>,
  ) => {
    try {
      const existingPartner = partnersList?.data?.find(
        (partner) => partner.referrerID === values.referrerID,
      );

      if (existingPartner) {
        helpers.setFieldError(
          "referrerID",
          `Referrer ID ${values.referrerID} already exists. Please use a different ID.`,
        );
        helpers.setSubmitting(false);
        return;
      }

      const payload = {
        referrerID: values.referrerID,
        username: values.username,
        password: values.password,
        partnerProgram: values.partnerProgram,
        partnerChannel: values.partnerChannel,
        partnerTitleKeys: values.partnerTitleKeys,
        disableRewardedAdsForDays: values.disableRewardedAdsForDays,
        platform: values.platform,
        device: values.device,
        invoiceParty: values.invoiceParty,
        publisherID: values.publisherID,
      };

      await createPartnerAsync({ payload });
      clearActionState();
    } catch (error) {
      const errMsg = (error as Error)?.message || "Something went wrong";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };
  return (
    <Box>
      <SectionHeader title="Campaigns" />
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderBottom: 0,
          backgroundColor: "background.paper",
          padding: "15px",
          borderRadius: "4px 4px 0 0",
        })}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, lg: 10 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <SearchFilter
                  filterFields={[filterFields[0]]}
                  maxWidth="100%"
                />
              </Grid>

              {filterFields.slice(1).map((field) => (
                <Grid key={field.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <SearchFilter filterFields={[field]} maxWidth="100%" />
                </Grid>
              ))}
            </Grid>

            {/* Partner Channel Dropdown - in a separate row */}
            <Grid container spacing={2} alignItems="center" sx={{ mt: "10px" }}>
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <TextField
                  select
                  label="Partner Channel"
                  value={selectedPartnerChannel}
                  onChange={(e) => handlePartnerChannelChange(e.target.value)}
                  fullWidth
                  size="medium"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  SelectProps={{
                    displayEmpty: true,
                  }}
                >
                  <MenuItem value="">All Partner Channels</MenuItem>
                  {partnerChannels.map((channel) => (
                    <MenuItem
                      key={channel.channelId}
                      value={String(channel.channelId)}
                    >
                      {channel.channelName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, lg: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent={{ xs: "flex-start", lg: "flex-end" }}
              sx={{
                flexWrap: "wrap",
                rowGap: 1,
                "& > *": { minHeight: 40 },
              }}
            >
              <ColumnVisibilityFilter
                value={visibleColumns as string[]}
                onChange={(vals) => setVisibleColumns(vals as ColumnKey[])}
                options={ColumnOptions}
                size="medium"
              />

              <Can do={"create"} on={"Campaigns"}>
                <Button
                  variant="contained"
                  sx={{ height: 38, padding: "0 20px" }}
                  onClick={() =>
                    setActionState({ action: FormActions.ADD, payload: {} })
                  }
                  startIcon={<Add />}
                >
                  Add New Campaign
                </Button>
              </Can>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <PartnerForm
        open={actionState.action === FormActions.ADD}
        onClose={clearActionState}
        title="Add Campaign"
        initialValues={actionState.payload}
        disable={isPending}
        onSubmit={handleSubmit}
        showCloseButton
      />
    </Box>
  );
};

export default PartnersHeader;
